import { PROJECTION_TOO_MANY_POINTS_STATUS_ID } from '@editor/config/constants'
import { hashString } from '@editor/hooks/useHashUpdater'
import { BlueprintV2 } from '@editor/interfaces/blueprint'
import { FileProcessingStatus } from '@editor/interfaces/file'

/**
 * Checks whether the given element has any ancestor with
 * data-prevent-exit-editing-mode="true".
 */
export const hasPreventExitEditingAncestor = (el: HTMLElement): boolean => {
  let current: HTMLElement | null = el.parentElement
  while (current) {
    if (current.dataset.preventExitEditingMode === 'true') {
      return true
    }
    current = current.parentElement
  }
  return false
}

/**
 * Checks if the blueprint has any PCD processing status as RUNNING.
 *
 * @param bp BlueprintV2 | undefined
 */
export const getRunningPcdStatus = (bp?: BlueprintV2) =>
  [
    bp?.project_3d_pcd_to_2d_pcd_status?.status,
    ...(Object.values(bp?.aligned_pcds_status || {}).map((s) => s.status) || []),
  ].filter((status) => status === FileProcessingStatus.RUNNING)

// status_id from a 3D→2D projection status. The error contract wraps a generic top-level
// status_id and carries the real detector id in status_message, so the nested id takes
// precedence (as in ErrorHandler); fall back to the top-level id when it is absent or invalid.
const getProjectionStatusId = (status?: BlueprintV2['project_3d_pcd_to_2d_pcd_status']): number | undefined => {
  if (!status) return undefined
  if (status.status_message?.includes('status_id')) {
    try {
      const nested = (JSON.parse(status.status_message) as { status_id?: number }).status_id
      if (nested) return nested
    } catch {
      // fall through to the top-level id
    }
  }
  return status.status_id || undefined
}

// True when the 3D→2D projection failed because the area has too many points (needs a 注目領域).
export const isProjectionTooManyPointsError = (bp?: BlueprintV2): boolean =>
  bp?.project_3d_pcd_to_2d_pcd_status?.status === FileProcessingStatus.FAILED &&
  getProjectionStatusId(bp.project_3d_pcd_to_2d_pcd_status) === PROJECTION_TOO_MANY_POINTS_STATUS_ID

/**
 * Gets the default selected blueprint from an array of blueprints.
 * It can either be defined via URL param (#bp<ID>) or the first blueprint in the array.
 *
 * @param blueprints Array of BlueprintV2
 */
export const getDefaultSelectedBlueprint = (blueprints: BlueprintV2[]): BlueprintV2 | undefined => {
  if (!blueprints.length) return undefined

  // Check URL param
  const currentHash = window.location.hash.replace(/^#/, '')
  const segments = currentHash ? currentHash.split('#') : []
  const found = segments.reduce<BlueprintV2 | undefined>((acc, hash) => {
    if (acc) return acc
    if (hash.startsWith('bp')) {
      const bpId = hash.replace('bp', '')
      return blueprints.find((bp) => hashString(bp.blueprint_id) === bpId) || acc
    }
    return acc
  }, undefined)
  if (found) return found

  // Default to first blueprint
  return blueprints[0]
}
